import { GameClickProps } from '@/components/modular-grid/types';
import config from '@/config';
import { HOMEPAGE_BASE_PATH } from '@/logic/main/constants';
import { isIframeWrapperEnabled } from '@/logic/main/utils';
import Cookie from '@/ports-frontend/Cookie';
import Location from '@/ports-frontend/Location';
import Tracking from '@/ports-frontend/Tracking';
import { BannerStandaloneItemClickParams } from '@/types/category/bannerStandaloneTypes';
import { GameMode, PortalVerticalGameTilePropTypes } from '@/types/gameTypes';
import {
  DepositPartialEvents,
  LoginPartialEvents,
  PromotionsPartialEvents,
  RegistrationPartialEvents,
  TrackingStorageType,
} from '@/types/partialEvents';
import { debounce } from '@/utils/lodash-alternatives';

import {
  DeviceInfo,
  LogicContext,
  PartialEventData,
  SearchInfo,
  SearchSourceTypeEnum,
  UserInitData,
} from './../logic/main/types';
import {
  ACCOUNT_BALANCE_NEEDING_EVENTS,
  COMMON_PARTIAL_DEPOSIT_EVENTS,
  COMMON_PARTIAL_LOGIN_EVENTS,
  COMMON_PARTIAL_PROMOTIONS_EVENTS,
  COMMON_PARTIAL_REGISTRATION_EVENTS,
  DEPOSIT_PARTIAL_EVENTS_MAP,
  LOGIN_PARTIAL_EVENTS_MAP,
  PARTIAL_DEPOSIT_EVENTS_MAP,
  PARTIAL_LOGIN_EVENTS_MAP,
  PARTIAL_PROMOTIONS_EVENTS_MAP,
  PARTIAL_REGISTRATION_EVENTS_MAP,
  PROMOTIONS_PARTIAL_EVENTS_MAP,
  REGISTRATION_PARTIAL_EVENTS_MAP,
} from './constants';

export interface GameTrackOptions {
  id: string;
  gameId: string;
  game: GameClickProps;
  slideIdx: number;
  sectionTitle: string;
  gamePosition: string;
  gameShuffleCount?: number;
  title: string;
  transaction_code: string;
  transactional_game_code: { desktop: string; mobile: string };
  presentation_code: string;
  launchUrl: string;
  gameDetailsPath: string;
  provider: string;
  gameMode: GameMode;
  searchSource?: SearchSourceTypeEnum;
}

interface GameInfo {
  gameMode?: string;
  sectionTitle?: string;
  elementType?: string;
  gamePosition?: string | number;
  type?: string;
  transactional_game_code?: { desktop: string; mobile: string };
  gameList?: string;
  trackingTitle?: string;
  sectionID?: string;
  gameShuffleCount?: number | undefined;
}
interface TrackingGamingRolloutType {
  date: string;
  split: number;
  countryNamePrefix: string;
  verticalName: string;
  segmentType: string;
}

interface TrackingSimplifiedGamingRolloutType {
  split: number;
  verticalName: string;
  segmentType: string;
}

let deviceStorage: DeviceInfo = {
  deviceType: 'mobile',
  isNative: false,
  nativeDeviceOs: null,
  deviceOs: null,
};

const gameTrackingDefaults = {
  ElementType: GameMode.Standard,
  GameCode: '',
  GameSupplier: '',
  ProductName: config.features.tracking.productContext,
  ProductAreaName: 'Gaming',
  SubProductName: 'NA', // TODO change in the future after specification
  GameTypeName: 'NA', // TODO change in the future after specification
  GameClassName: null,
  GameName: '',
  GameList: undefined,
  GameListPosition: [],
  GameCategory: '',
  GameVariant: 'NA', // TODO change in the future after specification
  GameShuffleCount: undefined,
};

const storage: TrackingStorageType = {
  general: {
    initLocation: {
      origin: '',
      hostname: '',
      pathname: '',
    },
  },
  delayedEvents: [],
  trackingInstalled: false,
  tracking: {
    SiteSubSection: undefined,
    SiteSectionCategory: null,
    LoginStatus: 'Not logged in',
    UserStatus: 'Non-Customer',
    CustomerAccountNumber: null,
    CurrencyCode: null,
    AppID: null,
    AppVersion: null,
    AppName: null,
    ApplicationType: 'Website',
    LandingDocumentLocation: '',
    PageTitle: undefined,
    PageUrl: undefined,
    ProductContext: null,
    SiteSection: null,
    AppUserId: null,
    PageGameName: null,
    SiteLanguage: 'en-gb',
    SiteSectionSubCategory: null,
    PersonalisationName: null,
    ContentCountry: null,
    ContentRegion: null,
  },
  // Updated with method "updateGameTrackingStorage" and  "resetGameTrackingStorage"
  gameTracking: { ...gameTrackingDefaults },
};

export function resetGameTrackingStorage() {
  storage.gameTracking = { ...gameTrackingDefaults };
}

export function updateGameTrackingStorage(
  gameInfo: (GameInfo & GameClickProps) | undefined,
  deviceInfo: DeviceInfo
) {
  if (!gameInfo) return;
  const { productContext } = config.features.tracking;
  if (gameInfo.gameMode) storage.gameTracking.ElementType = gameInfo.gameMode;
  if (gameInfo.sectionTitle)
    storage.gameTracking.GameList = gameInfo.sectionTitle;
  if (gameInfo.elementType)
    storage.gameTracking.ElementType = gameInfo.elementType;
  if (gameInfo.gamePosition)
    storage.gameTracking.GameListPosition = [`${gameInfo.gamePosition}`];
  if (gameInfo.type) storage.gameTracking.GameClassName = gameInfo.type;
  if (gameInfo.gameList) storage.gameTracking.GameList = gameInfo.gameList;
  if (gameInfo.transactional_game_code)
    storage.gameTracking.TransactionalGameCode =
      deviceInfo.deviceType === 'desktop'
        ? gameInfo.transactional_game_code.desktop
        : gameInfo.transactional_game_code.mobile;

  storage.gameTracking = {
    ...storage.gameTracking,
    GameCode: gameInfo.transaction_code,
    GameSupplier: gameInfo.provider,
    ProductName: productContext,
    GameVariant: gameInfo.sash?.title || 'NA',
    GameName: gameInfo.trackingTitle || gameInfo.title,
    GameCategory: `Gaming/${productContext}/${
      gameInfo.sectionID === 'recentlyPlayed'
        ? 'Recently Played'
        : gameInfo.type || 'Unknown'
    }/${storage.tracking.SiteSubSection}/${gameInfo.provider}`,
    ...(gameInfo.gameShuffleCount && gameInfo.gameShuffleCount >= 0
      ? { GameShuffleCount: gameInfo.gameShuffleCount }
      : null),
  };
}

const isTrackingStillDelayed = () => {
  return (
    config.features.tracking &&
    config.features.tracking.delayed &&
    !storage.trackingInstalled
  );
};

const updateWindowBusinessStore = debounce(() => {
  Tracking.updateStore(storage.tracking);
}, 100);

const userStorage = {
  AccountBalance: 0,
};

export function saveUserInitData(userData: UserInitData) {
  const { CurrencyCode, CustomerAccountNumber, UserStatus } = userData;
  storage.tracking.CustomerAccountNumber = CustomerAccountNumber;
  storage.tracking.CurrencyCode = CurrencyCode;
  storage.tracking.UserStatus = UserStatus;
  updateWindowBusinessStore();
}

const extractDomain = async () => {
  const location = await Location.get();
  const domainParts = location.hostname.split('.').reverse();
  const domain = `.${domainParts[1]}.${domainParts[0]}`;

  return domain;
};

const setCookie = async (key: string, value: string) => {
  const domain = await extractDomain();
  Cookie.set(key, value, { expires: 365, domain });
};

export function updateUserCurrency(currency: string) {
  if (storage.tracking.CurrencyCode === currency) {
    return;
  }

  storage.tracking.CurrencyCode = currency;
  setCookie('trk_curc', currency);
}

export function updateUserAccountNumber(accountNo: string) {
  if (storage.tracking.CustomerAccountNumber === accountNo) {
    return;
  }

  storage.tracking.CustomerAccountNumber = accountNo;
  setCookie('trk_uid', accountNo);
  storage.tracking.UserStatus = 'Customer';
  updateWindowBusinessStore();
}

export function updateUserBalance(balance: number) {
  userStorage.AccountBalance = balance;
}

export function updateLoginStatus(loggedIn: boolean) {
  storage.tracking.LoginStatus = loggedIn ? 'Logged in' : 'Not logged in';
  updateWindowBusinessStore();
}

export function saveTrackingDeviceInfo(deviceInfo: DeviceInfo) {
  const { isNative, nativeDeviceOs, appVersion } = deviceInfo;

  deviceStorage = deviceInfo;
  if (isNative) {
    storage.tracking.AppID = !nativeDeviceOs
      ? null
      : config.features.tracking.appIds[nativeDeviceOs];
    storage.tracking.AppVersion = appVersion;
    storage.tracking.AppName = config.features.tracking.appName;
    storage.tracking.ApplicationType = 'App';
  } else {
    storage.tracking.AppID = null;
    storage.tracking.AppVersion = null;
    storage.tracking.AppName = null;
    storage.tracking.ApplicationType = 'Website';
  }
  updateWindowBusinessStore();
}

export async function saveInitialTrackingData() {
  const location = await Location.get();

  const landingDocumentLocation = await getLandingDocumentLocation(location);
  const pageUrl = location.href;
  const pageTitle = parsePageTitle(location.pathname, location.hostname);
  storage.general.initLocation = {
    origin: location.origin,
    hostname: location.hostname,
    pathname: location.pathname,
  };
  storage.tracking.LandingDocumentLocation = landingDocumentLocation;
  storage.tracking.PageUrl = pageUrl;
  storage.tracking.PageTitle = pageTitle;
  storage.tracking.SiteLanguage =
    config.locale || storage.tracking.SiteLanguage;
  storage.tracking.ContentCountry =
    config.viewer_country || storage.tracking.ContentCountry;
  storage.tracking.ContentRegion =
    config.viewer_region || storage.tracking.ContentRegion;
}

export function saveSiteSpecificTrackingInfo(
  trackingData: LogicContext['trackingData'],
  path = '',
  segments: LogicContext['segments']
) {
  const pageData = trackingData?.page || storage.tracking;

  storage.tracking.ProductContext =
    trackingData?.ProductContext || storage.tracking.ProductContext;
  storage.tracking.SiteSection =
    trackingData?.SiteSection || storage.tracking.SiteSection;
  storage.tracking.PersonalisationName =
    segments || storage.tracking.PersonalisationName;

  storage.tracking.SiteSubSection = pageData?.SiteSubSection || null;
  storage.tracking.SiteSectionCategory = pageData?.SiteSectionCategory || null;
  storage.tracking.SiteSectionSubCategory =
    pageData?.SiteSectionSubCategory || null;
  storage.tracking.PageGameName = pageData?.PageGameName || null;

  const pagePath = config.history.enabled
    ? path.replace(/\/\//g, '/')
    : storage.general.initLocation.pathname
        .replace(`${config.version}`, '')
        .replace(/\/\//g, '/');

  const pageUrl = `${storage.general.initLocation.origin}${pagePath}`;
  const pageTitle = parsePageTitle(pagePath);
  storage.tracking.PageUrl = pageUrl;
  storage.tracking.PageTitle = pageTitle;
}

function parsePageTitle(path: string, hostname?: string) {
  const existingHostname = hostname || storage.general.initLocation.hostname;
  const hostnamePart = existingHostname.split('.')[0];
  const pathnameParts = path
    .split('/')
    .filter((str: string) => str && str !== storage.tracking.SiteLanguage);

  const elements = [
    hostnamePart,
    storage.tracking.SiteLanguage,
    ...pathnameParts,
  ];

  return elements.join('|');
}

async function getLandingDocumentLocation(location: {
  hostname: string;
  href: string;
}) {
  const actualCookieValue = await Cookie.get('ddl_landing_document_location');
  if (actualCookieValue) {
    return actualCookieValue;
  }

  const domainParts = location.hostname.split('.').reverse();
  const domain = `.${domainParts[1]}.${domainParts[0]}`;
  await Cookie.set('ddl_landing_document_location', location.href, {
    domain: domain,
    path: HOMEPAGE_BASE_PATH,
  });

  return location.href;
}

/**
 * 'jurisdiction' parameter cannot be deleted, it is required in Tracking data types
 *  however, 'profile' parameter when defined override 'jurisdiction' value
 *  https://gitlab.com/williamhillplc/customer-engagement/central-products/central-services/tracking-kit/-/blob/v0.4.1/src/install.js#L45
 */
export function installTracking() {
  if (!storage.trackingInstalled) {
    Tracking.install({
      profile: config.profile,
      jurisdiction: config.jurisdiction,
      environment: process.env.REACT_APP_ENVIRONMENT === 'prod' ? 'prd' : 'stg',
    });
    storage.trackingInstalled = true;
  }
}

export function sendTrackEvents() {
  if (!storage.trackingInstalled) {
    installTracking();
    storage.delayedEvents.forEach(event => Tracking.track(event));
    storage.delayedEvents = [];
  }
}

function manageTrackEventPublish(event: any) {
  if (isTrackingStillDelayed()) {
    storage.delayedEvents.push(event);
  } else {
    return Tracking.track(event);
  }
}

export function trackGamingRolloutVisit({
  date,
  split,
  countryNamePrefix,
  verticalName,
  segmentType,
}: TrackingGamingRolloutType) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'gaming_rollout',
      EventCategory: 'Optimizely',
      EventAction: `2577_Alchemy Rollout ${verticalName} ${countryNamePrefix}_${date}_Y`,
      EventLabel: `${segmentType}_Segment 1_Alchemy ${split}%`,
      ...storage.tracking,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackSimpleGamingRolloutVisit({
  split,
  verticalName,
  segmentType,
}: TrackingSimplifiedGamingRolloutType) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'gaming_rollout',
      EventCategory: 'Gaming Rollout',
      EventAction: `Alchemy Rollout ${verticalName}`,
      EventLabel: `${segmentType}_Segment 1_Alchemy ${split}%`,
      ...storage.tracking,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackViewChange() {
  const trackingEvent = {
    type: 'view',
    event: {
      tealium_event: 'pageview',
      ...storage.tracking,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackGameClick({
  gameParams,
  deviceInfo,
}: {
  gameParams: GameTrackOptions;
  deviceInfo: DeviceInfo;
}) {
  const {
    game,
    gamePosition,
    gameMode,
    sectionTitle,
    gameShuffleCount,
  } = gameParams;

  updateGameTrackingStorage(
    {
      sectionID: gameParams.id,
      ...game,
      gameMode,
      gamePosition,
      sectionTitle,
      gameShuffleCount,
    },
    deviceInfo
  );

  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'game_click',
      NonInteractionEvent: '0',
      EventCategory: 'Game Interactions',
      EventLabel: game.trackingTitle || game.title,
      EventAction: 'Game Launch',
      ...storage.gameTracking,
      ...storage.tracking,
      SiteSectionSubCategory: 'Game Launcher',
      SearchSource: gameParams?.searchSource || SearchSourceTypeEnum.NULL,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export function trackGameClickFromLauncher({
  game,
  deviceInfo,
}: {
  game: PortalVerticalGameTilePropTypes;
  deviceInfo: DeviceInfo;
}) {
  updateGameTrackingStorage(
    {
      ...game,
    },
    deviceInfo
  );

  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'game_click',
      NonInteractionEvent: '0',
      EventCategory: 'Game Interactions',
      EventLabel: game.title,
      EventAction: 'Game Launch',
      ...storage.gameTracking,
      ...storage.tracking,
      SiteSectionSubCategory: 'Game Launcher',
      SearchSource: SearchSourceTypeEnum.NULL,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export function trackPromoClick(url: string) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'promo_click',
      NonInteractionEvent: '0',
      EventCategory: 'Promotion Interactions',
      EventLabel: url,
      ElementType: 'Standard',
      EventAction: 'Promotion Click',
      ...storage.tracking,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export function trackDepositCTAFrameClick() {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'ingame_menu',
      NonInteractionEvent: '0',
      EventCategory: 'Menu Interactions',
      EventLabel: 'Make a deposit',
      ElementType: 'Deposit CTA Frame',
      EventAction: 'In-Game Menu',
      ...storage.tracking,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export function trackMenuClick({
  title,
  actionTitle,
}: {
  title?: string | null;
  actionTitle?: string;
}) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'menu_interaction',
      NonInteractionEvent: '0',
      EventCategory: 'Menu Interactions',
      EventLabel: title || storage.tracking.PageTitle,
      EventAction: actionTitle || 'In-Page Nav',
      ElementType: 'Standard',
      ...storage.tracking,
      SiteSectionSubCategory: null,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackStepClick({ title }: { title: string }) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'menu_interactions_poker',
      transport: 'beacon', // `navigator.sendBeacon` sends the event tracking action just before the user exits the page (opens a new window)
      NonInteractionEvent: '0',
      EventCategory: 'Menu Interactions',
      EventLabel: title,
      EventAction: 'Poker',
      ElementType: 'Standard',
      ...storage.tracking,
      SiteSectionSubCategory: null,
      SiteSectionCategory:
        storage.tracking.SiteSectionCategory === 'Featured'
          ? null
          : storage.tracking.SiteSectionCategory,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackBannerStandaloneClick({
  title,
  eventAction,
  tealiumEvent,
}: BannerStandaloneItemClickParams) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: tealiumEvent,
      transport: 'beacon', // `navigator.sendBeacon` sends the event tracking action just before the user exits the page (opens a new window)
      NonInteractionEvent: '0',
      EventCategory: 'Menu Interactions',
      EventLabel: title,
      EventAction: eventAction,
      ElementType: 'Standard',
      ...storage.tracking,
      SiteSectionSubCategory: null,
      SiteSectionCategory:
        storage.tracking.SiteSectionCategory === 'Featured'
          ? null
          : storage.tracking.SiteSectionCategory,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackShuffleClick({
  isGameShuffleStartAction,
}: {
  isGameShuffleStartAction: boolean;
}) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: isGameShuffleStartAction
        ? 'game_shuffle_widget_start'
        : 'game_shuffle_again_click',
      NonInteractionEvent: '0',
      EventCategory: 'Game Interactions',
      EventLabel: isGameShuffleStartAction ? 'Shuffle Start' : 'Shuffle Again',
      EventAction: 'Game Shuffle',
      ElementType: 'Widget Shuffle',
      ...storage.tracking,
      SiteSectionSubCategory: null,
      SiteSectionCategory:
        storage.tracking.SiteSectionCategory === 'Featured'
          ? null
          : storage.tracking.SiteSectionCategory,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackSearchResult({ search }: { search: SearchInfo }) {
  const resultsAmount = search.itemsNumber
    ? search.itemsNumber
    : search.searchItems.length;
  const searchFilter =
    search.currentType && search.searchItems.length ? search.currentType : null;
  const productContext = config.features.tracking.productContext;

  const trackingEvent = {
    type: 'view',
    event: {
      tealium_event: 'game_results',
      ElementType: 'Standard',
      EffortAttempt: search.attempts,
      ResultsAmount: resultsAmount,
      SearchTerm: search.searchPhrase.toLowerCase(),
      SearchFilter: searchFilter,
      SearchCategory: productContext,
      SearchSource: search.sourceType,
      GameList: 'Search Results',
      ...storage.tracking,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackOpenSearch() {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'game_search_open',
      NonInteractionEvent: '0',
      EventCategory: 'Game Search',
      EventLabel: 'Game Search Open',
      EventAction: 'Search Open',
      ElementType: 'Standard',
      ...storage.tracking,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

const createLoginEventsPayload = (name: LoginPartialEvents) => {
  return {
    ...COMMON_PARTIAL_LOGIN_EVENTS,
    ...PARTIAL_LOGIN_EVENTS_MAP[name],
  };
};
const createDepositEventsPayload = (
  name: DepositPartialEvents,
  deviceInfo: DeviceInfo
) => {
  const productSource =
    config.features.tracking.pmtSource?.[deviceInfo.deviceType];

  return {
    ...COMMON_PARTIAL_DEPOSIT_EVENTS,
    ...PARTIAL_DEPOSIT_EVENTS_MAP[name],
    ProductSource: productSource,
  };
};
const createPromotionsEventsPayload = (name: PromotionsPartialEvents) => ({
  ...COMMON_PARTIAL_PROMOTIONS_EVENTS,
  ...PARTIAL_PROMOTIONS_EVENTS_MAP[name],
});

const createRegistrationEventsPayload = (
  name: RegistrationPartialEvents,
  payload: any,
  deviceInfo: DeviceInfo
) => {
  const productSource =
    config.features.tracking.regSource?.[deviceInfo.deviceType];

  if (name === 'registration_step_unsuccessful') {
    return {
      ...COMMON_PARTIAL_REGISTRATION_EVENTS,
      ...PARTIAL_REGISTRATION_EVENTS_MAP[name],
      EventLabel: `Registration Step - ${payload.InteractionStep} - Unsuccessful`,
      productSource,
    };
  }
  return {
    ...COMMON_PARTIAL_REGISTRATION_EVENTS,
    ...PARTIAL_REGISTRATION_EVENTS_MAP[name],
    productSource,
  };
};

export function trackPartialEvent({
  name,
  payloadPartialEvent,
  isAccountInfoAvailable,
}: {
  name: any;
  payloadPartialEvent: PartialEventData;
  isAccountInfoAvailable: boolean;
}) {
  let transformedPayload;
  if (LOGIN_PARTIAL_EVENTS_MAP.includes(name)) {
    transformedPayload = createLoginEventsPayload(name);
  } else if (DEPOSIT_PARTIAL_EVENTS_MAP.includes(name)) {
    transformedPayload = createDepositEventsPayload(name, deviceStorage);
  } else if (PROMOTIONS_PARTIAL_EVENTS_MAP.includes(name)) {
    transformedPayload = createPromotionsEventsPayload(name);
  } else if (REGISTRATION_PARTIAL_EVENTS_MAP.includes(name)) {
    transformedPayload = createRegistrationEventsPayload(
      name,
      payloadPartialEvent,
      deviceStorage
    );
  }
  const accountInfoAvailable =
    isAccountInfoAvailable || isIframeWrapperEnabled();
  if (ACCOUNT_BALANCE_NEEDING_EVENTS.includes(name) && !accountInfoAvailable) {
    return {
      name,
      ...transformedPayload,
      ...payloadPartialEvent,
    };
  }
  if (ACCOUNT_BALANCE_NEEDING_EVENTS.includes(name)) {
    transformedPayload = {
      ...transformedPayload,
      AccountBalance: userStorage.AccountBalance,
    };
  }

  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: name,
      ...storage.tracking,
      ...payloadPartialEvent,
      ...transformedPayload,
    },
  };

  return manageTrackEventPublish(trackingEvent);
}

export function trackPendingAccountEvent({
  name,
  segments,
  ...payload
}: {
  name: string;
  segments?: string[];
  [propName: string]: any;
}) {
  storage.tracking.PersonalisationName = segments || null;

  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: name,
      ...storage.tracking,
      ...payload,
      ...userStorage,
      PersonalisationName: segments,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export const trackTrebuchetPartialEvent = (
  name: string,
  payloadPartialEvent: PartialEventData
) => {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: name,
      ...storage.gameTracking,
      ...storage.tracking,
      ...payloadPartialEvent,
    },
  };

  manageTrackEventPublish(trackingEvent);
};

export function trackIdlePopup() {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'gaming_idle_pop_up',
      NonInteractionEvent: '1',
      EventCategory: 'Menu Interactions',
      EventLabel: null,
      EventAction: 'Gaming Idle Pop-Up',
      ElementType: null,
      ...storage.tracking,
      SiteSectionSubCategory: null,
      SiteSectionCategory:
        storage.tracking.SiteSectionCategory === 'Featured'
          ? null
          : storage.tracking.SiteSectionCategory,
    },
  };
  manageTrackEventPublish(trackingEvent);
}

export function trackIdlePopupGame({ game }: { game: GameClickProps }) {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'gaming_idle_pop_up_game',
      NonInteractionEvent: '1',
      EventCategory: 'Game Interactions',
      EventLabel: game.trackingTitle || game.title,
      EventAction: 'Gaming Idle Pop-Up Game',
      ...storage.gameTracking,
      ...storage.tracking,
      SiteSectionSubCategory: null,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export function trackIdlePopupHideForSevenDays() {
  const trackingEvent = {
    type: 'link',
    event: {
      tealium_event: 'gaming_idle_pop_up_checkbox',
      NonInteractionEvent: '1',
      EventCategory: 'Game Interactions',
      EventLabel: null,
      EventAction: 'Gaming Idle Popup Hide for 7 Days',
      ...storage.gameTracking,
      ...storage.tracking,
      SiteSectionSubCategory: null,
    },
  };

  manageTrackEventPublish(trackingEvent);
}

export function trackFloatingIcon() {
  const trackingEvent = {
    type: 'view',
    event: {
      ...storage.tracking,
      tealium_event: 'floating_icon_click',
      SiteSectionSubCategory: 'Floating Icon',
    },
  };

  manageTrackEventPublish(trackingEvent);
}
